import { apiApplicants } from "@/api/recruit/applicant.api.ts";
import {
  useModals,
  Modal,
  ModalProps,
  parseUrlHelper,
  Button,
  FontAwesomeIcon,
  Stack,
  Text,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useApplicantCreate } from "@/queries/recruit/use-applicants.query.ts";
import { useCandidateCreate } from "@/queries/recruit/use-candidates.query.ts";
import { useJobDetails } from "@/queries/recruit/use-jobs.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { PipelineStageTypes } from "@/types/recruit/stages.types.ts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

interface FileUploadStatus {
  file: File;
  status: "pending" | "uploading" | "success" | "error";
  error?: string;
}

interface BulkUploadModalProps extends ModalProps {
  files: File[];
}

export const BulkUploadCVModal = ({ files: initialFiles, ...props }: BulkUploadModalProps) => {
  const { t } = useTranslation();
  const { id } = { ...props };
  const { renderFileSize } = useFormatter();
  const [isUploading, setIsUploading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const { close } = useModals();

  const applicantCreate = useApplicantCreate();
  const candidateCreate = useCandidateCreate();

  const [searchParams] = useSearchParams();
  const jobId = parseUrlHelper.getNumberArray(searchParams, "job_id");
  const jobIdSelected = jobId && jobId.length == 1 ? jobId[0] : undefined;
  const { data: jobDetails } = useJobDetails(jobIdSelected);

  const [files, setFiles] = useState<FileUploadStatus[]>(
    initialFiles.map((file) => ({
      file,
      status: "pending",
    }))
  );
  const uploadFiles = async () => {
    if (isUploading || isCompleted) return;

    setIsUploading(true);

    for (let i = 0; i < files.length; i++) {
      const fileStatus = files[i];

      setFiles((prev) => prev.map((f, index) => (index === i ? { ...f, status: "uploading" } : f)));

      const formData = new FormData();
      formData.append("file", fileStatus.file, fileStatus.file.name);
      const { data } = await apiApplicants.uploadFile(formData);

      if (data && data.applicant) {
        const createPayload = {
          new_applicant: data.applicant,
          file_uuids: data.files.map((f) => f.uuid),
        };

        const applicantSaved = await applicantCreate.mutateAsync(createPayload);

        analyticsService.trackEvent(analyticEvents.applicants.created, {
          [analyticProperties.id]: applicantSaved.id,
          [analyticProperties.source]: "App",
          [analyticProperties.mode]: "Bulk",
        });

        if (jobDetails) {
          candidateCreate.mutate(
            {
              job_id: jobDetails.id,
              applicant_id: applicantSaved.id,
              pipeline_stage_id:
                jobDetails.pipeline.stages.find(
                  (stage) => stage.type === PipelineStageTypes.applied
                )?.id || jobDetails.pipeline.stages[0].id,
              pipeline_id: jobDetails.pipeline.id,
            },
            {
              onSuccess: (candidate) => {
                analyticsService.trackEvent(analyticEvents.applicants.assignedToJob, {
                  [analyticProperties.id]: candidate.id,
                  [analyticProperties.source]: "App",
                  [analyticProperties.mode]: "Bulk",
                });
              },
            }
          );
        }

        setFiles((prev) =>
          prev.map((f, index) =>
            index === i
              ? {
                  ...f,
                  status: "success",
                  applicant: data.applicant,
                }
              : f
          )
        );
      } else {
        setFiles((prev) =>
          prev.map((f, index) =>
            index === i
              ? {
                  ...f,
                  status: "error",
                }
              : f
          )
        );
      }
    }

    setIsUploading(false);
    setIsCompleted(true);
  };

  useEffect(() => {
    if (jobIdSelected && !jobDetails) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect -- async upload starts once the selected job is loaded
    uploadFiles();
  }, [jobDetails, jobIdSelected]);

  const getStatusIcon = (status: FileUploadStatus["status"]) => {
    switch (status) {
      case "success":
        return <FontAwesomeIcon icon="fa-light fa-check" className="text-positive" />;
      case "error":
        return <FontAwesomeIcon icon="fa-light fa-times" className="text-warning" />;
      case "uploading":
        return (
          <FontAwesomeIcon icon="fa-light fa-spinner" className="animate-spin text-secondary" />
        );
      default:
        return <FontAwesomeIcon icon="fa-light fa-timer" className="text-secondary" />;
    }
  };

  return (
    <Modal
      variant={"middle"}
      title={t("ats:uploading_cvs")}
      withCloser={true}
      {...props}
      actions={
        <Stack gap="sm" className="w-full">
          {isCompleted && (
            <Button
              size="lg"
              className="w-full"
              onClick={() => {
                close(id);
              }}
            >
              {t("common:ok")}
            </Button>
          )}

          {isUploading && (
            <Button
              size="lg"
              variant="secondary"
              className="w-full"
              onClick={() => {
                close(id);
              }}
            >
              {t("common:cancel")}
            </Button>
          )}
        </Stack>
      }
    >
      <Stack>
        <Stack gap={"sm"}>
          {files.map((file, index) => (
            <Stack key={`applicant-file-${index}`} className={"w-full"}>
              <Stack
                direction={"horizontal"}
                gap={"sm"}
                justify={"between"}
                items={"center"}
                className={"bg-dark/5 rounded-xs px-2 py-1 overflow-hidden grow-0"}
              >
                <Stack direction={"horizontal"} gap={"sm"}>
                  <Text
                    className={twMerge(
                      "overflow-hidden text-ellipsis whitespace-nowrap",
                      file.status == "success" ? "text-brand-dark" : "text-secondary"
                    )}
                  >
                    {file.file.name}
                  </Text>
                  <Text className={"shrink-0 text-secondary"}>
                    {renderFileSize(file.file.size)}
                  </Text>
                </Stack>
                <Stack direction={"horizontal"} items={"center"} gap={"sm"} className={"shrink-0"}>
                  {getStatusIcon(file.status)}
                </Stack>
              </Stack>
            </Stack>
          ))}
        </Stack>
        {isUploading && (
          <Text className={"font-semibold text-warning pt-(--gap-lg)"}>
            {t("ats:uploading_cvs_count", { count: files.length })}
          </Text>
        )}
        {isCompleted && (
          <Text className={"font-semibold text-positive pt-(--gap-lg)"}>
            {t("ats:upload_success_count", { count: files.length })}
          </Text>
        )}
      </Stack>
    </Modal>
  );
};
